import * as THREE from 'three';
import { setKeyframeToBegin, setKeyframeToEnd} from "../Manager/SceneManager";
import {archMatrix, getTimes, getPonticsData} from "./AnimationHandler";
import {setMeshToLayer} from "./LayerHandler";
import {Store} from "../../../Application/Store/Store";
import { initPosition} from "../../Mesh/Handler/Grid";

import {toothMalocMaterial, toothSetupMaterial} from "../Provider/MaterialProvider";
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import {createOverlayTeeth} from "./OverlayHandler";
import { nextRenderTick } from '../Manager/InitializationOrchestrator';
import { reparentToMandiGroup } from 'Domain/Scene/Handler/MandiAdjustHandler';

export let malocTeethGroup
export const stateSetup = {
    setupReady: false,
    setupTeethGroup: null
};
const waitNextFrame = async (frames = 1) => {
    for (let i = 0; i < frames; i++) await nextRenderTick();
};

/**
 * Clone un mesh et l’ajoute au groupe de comparaison.
 * Selon l’arch (maxillaire ou mandibulaire), il définit le layer correspondant
 * et, pour les dents mandibulaires de type 0, l’ajoute aussi à la liste allMandiTeeth.
 */
export const addMeshInComparativeGroup = (mesh, arch) => {
    let meshCloned = mesh.clone();
    if (arch === "mandi"){
        setMeshToLayer(meshCloned, "comparativeMandi"); //Mandi
        if (mesh.userData.type === 0) {
            threeEngine.getAllMandiTeeth().push(meshCloned);
        }
    }
    else {
        setMeshToLayer(meshCloned, "comparativeMaxi"); //Maxi
    }
    const comparativeSceneGroup = threeEngine.getComparativeSceneGroup();
    if (comparativeSceneGroup) {
        comparativeSceneGroup.add(meshCloned);
    }
}

/**
 * Clone toutes les dents d’un groupe importé.
 * @param {THREE.Group} importedMeshesGroup - Le groupe d’origine (avec les dents).
 * @param {string} suffix - "Maloc" ou "Setup".
 * @param {string} layerPrefix - "maloc" ou "setup".
 * @param addLayer
 * @returns {THREE.Group} - Le nouveau groupe cloné.
 */
export function cloneTeethGroup(importedMeshesGroup, suffix, layerPrefix) {
    const group = new THREE.Group();
    group.name = `${suffix}TeethGroup`;

    importedMeshesGroup.traverse((child) => {
        if (child.userData.type !== 0) return;
        const clonedChild = child.clone();
        const toRemove = [];

        clonedChild.traverse((c) => {
            if (c === clonedChild) return;

            if (c.userData.type === 2) {
                // Renommage de l’attachement
                const [tooth, id1, id2] = c.name.split("_");
                c.name = `${tooth}_${suffix}${id1}_${id2}`;

                // Placement dans le bon layer
                const arch = archMatrix[c.userData.jawType];
                setMeshToLayer(c, `${layerPrefix}${arch === "maxi" ? "Maxi" : "Mandi"}`);

                // NOTE: Ne pas ajouter aux allAttachments globaux pour éviter que
                // updateAttachmentsVisibility (animation) ne masque les attachments comparatifs/setup.
            }else
             {
                toRemove.push(c);
            }
        });

        // Supprimer les objets inutiles
        toRemove.forEach(c => c.parent?.remove(c));

        // Renommer la dent principale
        const [tooth, id1, id2] = child.name.split("_");
        clonedChild.name = `${tooth}_${suffix}${id1}_${id2}`;
        if(clonedChild.userData.jawType === 'L'){
            reparentToMandiGroup(clonedChild); // Pour le ByteJump — rattache au mandiGroup en préservant la position world
        } else {
            // Ajouter au groupe final (seulement pour les dents maxi — les dents mandi sont dans mandiGroup)
            group.add(clonedChild);
        }

        // Layer sur la dent principale
        const archTooth = archMatrix[clonedChild.userData.jawType];
        setMeshToLayer(clonedChild, `${layerPrefix}${archTooth === "maxi" ? "Maxi" : "Mandi"}`);
    });

    // 2. Clone Pontics directement depuis le store
    const ponticsData = getPonticsData();
    if (ponticsData) {
        Object.entries(ponticsData).forEach(([position, steps]) => {
            const keys = Object.keys(steps).map(Number).sort((a,b)=>a-b);
            if (keys.length === 0) return;

            let shouldClone = false;
            let ponticToClone = null;

            if (layerPrefix === "maloc" || layerPrefix === "Maloc") {
                ponticToClone = steps[0] || steps[keys[0]]; // fallback on first available if 0 is missing
                if (ponticToClone) shouldClone = true;
            }
            else if (layerPrefix === "Setup" || layerPrefix === "setup") {
                const maxKey = keys[keys.length - 1];
                ponticToClone = steps[maxKey];
                if (ponticToClone) shouldClone = true;
            }

            if (shouldClone && ponticToClone) {
                const clonedChild = ponticToClone.clone();
                clonedChild.visible = true;
                
                const arch = archMatrix[clonedChild.userData.jawType];
                
                if (clonedChild.userData.jawType === 'L') {
                    const mandiGroup = threeEngine.getMandiGroup();
                    if (mandiGroup) {
                        mandiGroup.add(clonedChild);
                    } else {
                        group.add(clonedChild);
                    }
                } else {
                    group.add(clonedChild);
                }
                
                // Appliquer le layer à tous les sous-meshes
                const targetLayer = `${layerPrefix}${arch === "maxi" ? "Maxi" : "Mandi"}`;
                clonedChild.traverse((c) => {
                    setMeshToLayer(c, targetLayer);
                });
            }
        });
    }

    return group;
}


/**
 * Clone et prépare les dents pour la vue comparative (maloc + setup)
 */
export async function cloneMalocAndSetupForComparativeView(scene) {
    await nextRenderTick();

    const importedMeshesGroup = scene.getObjectByName("importedMeshes");
    console.table(importedMeshesGroup)
    console.log("les imports")
    if (!importedMeshesGroup) return;

    // Clone Maloc
    createOverlayTeeth(importedMeshesGroup, "Maloc", toothMalocMaterial);
    malocTeethGroup = cloneTeethGroup(importedMeshesGroup, "Maloc", "maloc");
    const engineScene = threeEngine.getScene();
    if (engineScene) {
        engineScene.add(malocTeethGroup);
    }

    // Clone Setup uniquement si le setup n'est pas encore prêt (Soit s'il n'y a pas de grille/grid)
    if (!stateSetup.setupReady) {
        initPosition();


        await waitNextFrame(5);

        setKeyframeToEnd(getTimes());
        await nextRenderTick();

        createOverlayTeeth(importedMeshesGroup, "Setup", toothSetupMaterial);

        stateSetup.setupTeethGroup = cloneTeethGroup(importedMeshesGroup, "Setup", "setup");
        stateSetup.setupReady = true;
        if (engineScene) {
            engineScene.add(stateSetup.setupTeethGroup);
        }

        await nextRenderTick();

        Store.dispatch(setKeyframeToBegin());
        attachTeethToMandiGroup();
    }
}


/**
 * Attache les dents mandibulaires au groupe global
 */
export function attachTeethToMandiGroup() {
    threeEngine.getAllMandiTeeth().forEach(mesh => reparentToMandiGroup(mesh));
}
